import { useContext, useEffect, useState, useMemo } from "react";
import { Chart } from "react-google-charts";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { useResizeDetector } from "react-resize-detector";
import DashboardService from "../../../../services/DashboardService";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact/skeleton";

const useIsLargeScreen = () => {
  const [isLargeScreen, setIsLargeScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );

  useEffect(() => {
    const handleChange = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    window.addEventListener("resize", handleChange);
    window.addEventListener("orientationchange", handleChange);
    document.addEventListener("fullscreenchange", handleChange);
    handleChange();
    return () => {
      window.removeEventListener("resize", handleChange);
      window.removeEventListener("orientationchange", handleChange);
      document.removeEventListener("fullscreenchange", handleChange);
    };
  }, []);

  return isLargeScreen;
};

const validateChartData = (data) => {
  if (!data || data.length === 0) return data;

  return data.map((row, index) => {
    if (index === 0) return row;

    return row.map((cell, cellIndex) => {
      if (cellIndex === 1) {
        const num = typeof cell === 'string' ? Number(cell) : cell;
        return isNaN(num) ? 0 : num;
      }
      return cell;
    });
  });
};

const Catalogueschart = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);
  const isLargeScreen = useIsLargeScreen();


  const { ref: containerRef, width: rawWidth } = useResizeDetector({
    refreshMode: "debounce",
    refreshRate: 100,
    skipOnMount: false,
  });

  const containerWidth = rawWidth ? Math.round(rawWidth / 10) * 10 : 0;

  const [selectedOption, setSelectedOption] = useState("incidentRepo");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedCourseId = useMemo(() => {
    return selectedCourse === 'ALL' || selectedCourse === ''
      ? 0
      : apiData?.courseData?.find(f => f.courseCode === selectedCourse)?.courseId ?? 0;
  }, [selectedCourse, apiData]);

useEffect(() => {
    const controller = new AbortController(); 

    const fetchCatalogueData = async () => {
      setLoading(true);
      try {
        const response = await DashboardService.GetCatalogueChart(
          selectedCourseId,
          authContext,
          controller.signal 
        );

        if (controller.signal.aborted) return; 

        if (!response) return;
        if (response.isSuccess && response.data) {
          const cleanData = {
            ...response.data,
            incidentData: {
              ...response.data.incidentData,
              totalIncidents: Number(response.data.incidentData.totalIncidents) || 0,
              categoryData: (response.data.incidentData.categoryData || []).map(item => ({
                ...item,
                count: Number(item.count) || 0
              }))
            },
            courseData: (response.data.courseData || []).map(item => ({
              ...item,
              totalIncidents: Number(item.totalIncidents) || 0
            })),
            courseCategoryData: (response.data.courseCategoryData || []).map(item => ({
              ...item,
              count: Number(item.count) || 0
            }))
          };
          setApiData(cleanData);
        } else {
          setError("Failed to fetch catalogue data");
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        console.error(err);
        setError("Failed to fetch catalogue data");
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchCatalogueData();

    return () => controller.abort(); 
  }, [selectedCourseId, authContext]);

  const { leftData, rightData } = useMemo(() => {
    if (!apiData) return { leftData: [], rightData: [] };

    const incidentRepoLeft = [
      ["Type", "Count", { role: "style" }],
      ["Total", Number(apiData.incidentData.totalIncidents) || 0, "#0F3D8C"],
    ];

    const incidentRepoRight = [
      ["Category", "Count"],
      ...(apiData.incidentData.categoryData || []).map((c) => [
        c.categoryName,
        Number(c.count) || 0
      ]),
    ];

    const allCoursesLeft = [
      ["Course", "Count", { role: "style" }],
      ...(apiData.courseData || []).map((c, i) => [
        c.courseName,
        Number(c.totalIncidents) || 0,
        ["#0F3D8C", "#4BB6FF", "#157ABE", "#7DC9FF"][i % 4],
      ]),
    ];

    const mergedCategories = {};
    (apiData.courseCategoryData || []).forEach((c) => {
      mergedCategories[c.categoryName] =
        (Number(mergedCategories[c.categoryName]) || 0) + (Number(c.count) || 0);
    });
    const allCoursesRight = [
      ["Category", "Count"],
      ...Object.entries(mergedCategories).map(([category, count]) => [
        category,
        Number(count) || 0
      ]),
    ];

   const selectedCourseData = (apiData.courseData || []).find(
  (c) => c.courseCode === selectedCourse
  );

    const selectedCourseLeft = selectedCourseData
      ? [
          ["Course", "Count", { role: "style" }],
          [
           selectedCourseData.courseName, 
            Number(selectedCourseData.totalIncidents) || 0,
            "#0F3D8C",
          ],
        ]
      : [
          ["Course", "Count", { role: "style" }],
          ["-", 0, "#0F3D8C"],
        ];

    const selectedCourseRight = [
  ["Category", "Count"],
  ...(apiData.courseCategoryData || [])
    .filter((c) => c.courseCode === selectedCourse)
    .map((c) => [c.categoryName, Number(c.count) || 0]),
];

    let left = [];
    let right = [];

    if (selectedOption === "incidentRepo") {
      left = validateChartData(incidentRepoLeft);
      right = validateChartData(incidentRepoRight);
    } else if (selectedOption === "allCourse") {
      if (!selectedCourse) {
        left = validateChartData(allCoursesLeft);
        right = validateChartData(allCoursesRight);
      } else {
        left = validateChartData(selectedCourseLeft);
        right = validateChartData(selectedCourseRight);
      }
    }

    if (left.length <= 1) {
      left = [["Type", "Count", { role: "style" }], ["No Data", 0, "#CCCCCC"]];
    }

    if (right.length <= 1) {
      right = [["Category", "Count"], ["No Data", 0]];
    }

    return { leftData: left, rightData: right };
  }, [apiData, selectedOption, selectedCourse]);

  if (error) return <div style={{ padding: 14, color: "red" }}>{error}</div>;

  if (loading) {
    return (
      <div
        className="charts"
        ref={containerRef}
        style={{
          borderRadius: 12,
          padding: isLargeScreen ? 16 : 12,
          background: isDark ? "#1e1e1e" : "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          gap: isLargeScreen ? 15 : 8,
          width: "100%",
          height: isLargeScreen ? "357px" : "auto",
          transition: "all 0.2s ease-out",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div>
            <Skeleton width="120px" height="18px" className="mb-1 mb-md-2" />
            <Skeleton width="150px" height="14px" />
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            <Skeleton width="100px" height="28px" className="mt-2 mt-md-0" />
            <Skeleton width="120px" height="28px" className="mt-2 mt-md-0" />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: isLargeScreen ? "row" : "column", gap: isLargeScreen ? 15 : 8 }}>
          <Skeleton width="100%" height={isLargeScreen ? "260px" : "150px"} />
          <Skeleton width="100%" height={isLargeScreen ? "260px" : "150px"} />
        </div>
      </div>
    );
  }

  if (!apiData) return null;

  const columnChartHeight = isLargeScreen ? "225px" : "180px";
  const rightChartMaxHeight = isLargeScreen ? 230 : 150;
  const cardPadding = isLargeScreen ? 16 : 12;
  const cardGap = isLargeScreen ? 15 : 8;
  const topFont = isLargeScreen ? 16 : 14;
  const subFont = isLargeScreen ? 13 : 11;
  const selectFont = isLargeScreen ? 11 : 11;

  const commonOptions = {
    backgroundColor: "transparent",
    chartArea: {
      width: isLargeScreen ? "85%" : "95%",
      height: isLargeScreen ? "70%" : "75%",
    },
    legend: { position: "none" },
    hAxis: {
      textStyle: { color: isDark ? "#eaeaea" : "#444", fontSize: selectFont, fontName: "Poppins" },
      gridlines: { color: isDark ? "#444" : "#eee" },
    },
    vAxis: {
      textStyle: { color: isDark ? "#eaeaea" : "#444", fontSize: selectFont, fontName: "Poppins" },
      gridlines: { color: isDark ? "#444" : "#eee" },
    },
    tooltip: { textStyle: { fontSize: isLargeScreen ? 11 : 10, fontName: "Poppins" } },
    bar: { groupWidth: "20%" },
  };

  const rightOptions = {
    backgroundColor: "transparent",
    chartArea: {
      width: isLargeScreen ? "70%" : "90%",
      height: "100%",
      left: isLargeScreen ? 150 : 100,
    },
    legend: "none",
    hAxis: {
      minValue: 0,
      textStyle: { color: isDark ? "#eaeaea" : "#444", fontSize: selectFont, fontName: "Poppins" },
    },
    tooltip: { textStyle: { fontSize: isLargeScreen ? 11 : 10, fontName: "Poppins" } },
    vAxis: {
      textStyle: { color: isDark ? "#eaeaea" : "#444", fontSize: selectFont, fontName: "Poppins" },
    },
    bar: { groupWidth: "30%" },
    colors: ["#157ABE"],
  };

  
  const leftChartKey = `left-${isLargeScreen}-${containerWidth}`;
  const rightChartKey = `right-${isLargeScreen}-${containerWidth}-${rightData.length}`;

  return (
    <div
      className="charts"
      ref={containerRef}
      style={{
        borderRadius: 12,
        padding: cardPadding,
        background: isDark ? "#1e1e1e" : "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        gap: cardGap,
        width: "100%",
        height: isLargeScreen ? "357px" : "auto",
        fontFamily: "Poppins, sans-serif",
        transition: "padding 0.2s ease-out, height 0.2s ease-out", // Smooth resize
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: topFont, color: isDark ? "#eaeaea" : "#000", fontFamily: "Poppins, sans-serif" }}>
            Catalogue
          </div>
          <p style={{ fontSize: subFont, color: isDark ? "#eaeaea" : "#6c757d" }}>
            Total Incidents: <b>{apiData.incidentData.totalIncidents || 0}</b>
          </p>
        </div>

        <div style={{ gap: "6px", flexWrap: "wrap" }}>
          <select
            value={selectedOption}
            onChange={(e) => {
              setSelectedOption(e.target.value);
              setSelectedCourse("");
            }}
            style={{
              fontSize: "12px",
              padding: isLargeScreen ? "4px 8px" : "3px 6px",
              borderRadius: 4,
              border: "1px solid #ddd",
              background: isDark ? "#333" : "#fff",
              color: isDark ? "#eaeaea" : "#000",
            }}
          >
            <option value="incidentRepo">Incident Repo</option>
            <option value="allCourse">All Courses</option>
          </select>

          {selectedOption === "allCourse" && (
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="ms-2"
              style={{
                fontSize: "12px",
                padding: isLargeScreen ? "4px 8px" : "3px 6px",
                borderRadius: 4,
                border: "1px solid #ddd",
                background: isDark ? "#333" : "#fff",
                color: isDark ? "#eaeaea" : "#000",
                maxWidth: 100,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <option value="">All</option>
              {(apiData?.courseData || []).map((c) => (
           <option key={c.courseCode} value={c.courseCode}>
              {c.courseName}
          </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isLargeScreen ? "row" : "column",
          gap: isLargeScreen ? 0 : 8,
          transition: "all 0.2s ease-out",
        }}
      >
        <div style={{ flex: 1, marginBottom: isLargeScreen ? 0 : 10 }}>
          <Chart
            key={leftChartKey}
            chartType="ColumnChart"
            width="100%"
            height={columnChartHeight}
            data={leftData}
            options={commonOptions}
          />
        </div>

        <div
          className="scroll-no-buttons"
          style={{
            flex: 1,
            maxHeight: rightChartMaxHeight,
            overflowY: "auto",
            scrollbarWidth: "thin",
            transition: "max-height 0.2s ease-out",
          }}
        >
          <Chart
            key={rightChartKey}
            chartType="BarChart"
            width="100%"
            height={`${Math.max(rightData.length * (isLargeScreen ? 35 : 28), 150)}px`}
            data={rightData}
            options={{
              ...rightOptions,
              chartArea: {
                width: isLargeScreen ? "70%" : "90%",
                height: "100%",
                left: isLargeScreen ? 150 : 100,
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Catalogueschart;